import i18next from 'i18next';
import {
    DateUtils,
    getDeeplinkURL,
    getDeeplinkQueryURL,
    HotelRoomUtils,
    ABUtils,
    Config,
    URLUtils,
    getDeeplinkSearchURL,
    getMFEDeeplinkURL,
} from '../utils';
import {
    TripType,
    TripTypeShort,
    FlightClass,
    EntryPoint,
    DpPaxRestrictionPropsDefault,
    SearchProp,
    SearchProducts,
    BuyPath,
    GeoType,
} from '../models';
import { isInspirationalSearch } from '../redux/currentSearch/selectors';
import { searchConfigByProduct } from './searchConfig/searchConfigByProduct';
import { composeHotelStandaloneParams } from './searchParameters/hotelStandaloneSearchParams';
import { composeHotelStandaloneByRegionParams } from './searchParameters/hotelStandaloneSearchByRegionParams';
import { composeDynpackParams } from './searchParameters/dynpackSearchParams';
import { composeFlightsParams } from './searchParameters/flightsSearchParams';

const SEARCH = {
    PARAMS_PARSER: {
        class: 'cabinClass',
        direct: 'isDirect',
        resident: 'isResident',
        adults: 'adults',
        children: 'kids',
        infants: 'infants',
    },
};

const searchParamsDefaults = {
    tripType: TripType.ROUND_TRIP,
    cabinClass: FlightClass.TOURIST,
    isDirect: false,
    pax: {
        adults: 1,
        kids: 0,
        infants: 0,
    },
    isResident: false,
};

const { defaultAdults } = DpPaxRestrictionPropsDefault;

const searchParamsSegmentParser = (data, tripType) => {
    let segments = [];

    let fillSegment = (key) => {
        const re = new RegExp('^(from|to|dep|ret|X|Y)(\\d+)?(\\.([a-zA-Z]+))?$');
        const result = re.exec(key);
        switch (result[1]) {
            case 'from':
            case 'to':
                fillSegmentLocation({
                    type: result[1],
                    idx: result[2],
                    value: data[key],
                    key: result[4],
                });
                if (tripType === TripType.ROUND_TRIP) {
                    const switchLocation = {
                        from: 'to',
                        to: 'from',
                    };
                    fillSegmentLocation({
                        type: switchLocation[result[1]],
                        idx: 1,
                        value: data[key],
                        key: result[4],
                    });
                }
                break;
            default:
                fillSegmentDate({
                    type: result[1],
                    idx: result[2],
                    value: data[key],
                    key: result[4],
                });
                break;
        }
    };

    let fillSegmentLocation = ({ type, idx = 0, value, key }) => {
        const keyMap = {
            from: 'origin',
            to: 'destination',
        };
        let segment = segments[idx] || {};
        let location = segment[keyMap[type]] || {};
        if (!key) {
            if (value.match(/^\d+$/)) {
                location.geoNodeId = parseInt(value, 10);
            } else {
                location.iata = value.toUpperCase();
            }
        } else {
            if (key === 'id') {
                key = 'geoNodeId';
            }
            location[key] = value;
        }
        segment[keyMap[type]] = location;
        segments[idx] = segment;
    };

    let fillSegmentDate = ({ type, idx = 0, value, key }) => {
        switch (type) {
            case 'ret':
                if (tripType !== TripType.ROUND_TRIP) {
                    return;
                }
                idx = 1;
                break;
            case 'X':
                if (tripType === TripType.MULTIPLE) {
                    return;
                }
                idx = 0;
                value = composeParametrizedDate('dep', value, segments[0].date);
                break;
            case 'Y':
                if (tripType !== TripType.ROUND_TRIP) {
                    return;
                }
                idx = 1;
                value = composeParametrizedDate('ret', value, segments[0].date);
                break;
        }

        let date;
        let segment = segments[idx] || {};
        if (DateUtils.isValidDate(DateUtils.parse(value))) {
            date = DateUtils.parse(value);
        }
        if (key) {
            const keyMap = {
                timewindow: 'timeWindow',
                time: 'time',
            };
            segment[keyMap[key]] = date;
        } else {
            segment.date = date;
        }
        segments[idx] = segment;
    };
    const regexpRooms = new RegExp('^(from|to|dep|ret|X|Y)');
    const segmentKeys = Object.keys(data).filter((key) => key.match(regexpRooms));
    segmentKeys.forEach((key) => fillSegment(key));
    segments.map((segment, index) => {
        if (!segment.id) {
            segment.id = Date.now() + index;
        }
    });
    return segments;
};
const searchParamsRoomsParser = (data, activeProduct) => {
    let rooms = [];
    let fillRoom = (key) => {
        const re = new RegExp('^room([0-4])*(Adults|Kid|Infant)([0-4])(Age)*$');
        const result = re.exec(key);

        let room = {};
        let idx = 0;
        if (result[2] === 'Adults') {
            idx = parseInt(result[3]);
            room =
                rooms[idx] ||
                Object.assign({}, HotelRoomUtils.getModelRoom(defaultAdults, [], idx));
            room.adults = parseInt(data[key]);
        } else {
            idx = parseInt(result[1]);
            room =
                rooms[idx] ||
                Object.assign({}, HotelRoomUtils.getModelRoom(defaultAdults, [], idx));
            room.kids.push(parseInt(data[key]));
        }
        rooms[idx] = room;
    };
    if (activeProduct === EntryPoint.DP) {
        const regexpRooms = new RegExp('^room');
        const roomKeys = Object.keys(data).filter((key) => key.match(regexpRooms));
        roomKeys.forEach((key) => fillRoom(key));
        if (rooms.length === 0) {
            rooms = [HotelRoomUtils.getDefaultRoom(DpPaxRestrictionPropsDefault)];
        }
    }
    return rooms;
};

const fillSeparateStay = (checkIn, checkOut, activeProduct) => {
    let separateStay = {
        enabled: false,
    };
    const checkInDate = DateUtils.parse(checkIn);
    const checkOutDate = DateUtils.parse(checkOut);
    if (
        activeProduct === EntryPoint.DP &&
        DateUtils.isValidDate(checkInDate) &&
        DateUtils.isValidDate(checkOutDate)
    ) {
        separateStay = {
            enabled: true,
            checkInDate,
            checkOutDate,
        };
    }
    return separateStay;
};

const searchParamsParser = (data, activeProduct = EntryPoint.FLIGHTS) => {
    let keyMap = SEARCH.PARAMS_PARSER;
    let search = Object.assign({}, searchParamsDefaults);

    search.tripType =
        activeProduct === EntryPoint.DP
            ? TripType.ROUND_TRIP
            : TripTypeShort[data.type] || search.tripType;
    search.isHotel = activeProduct === EntryPoint.DP;
    search.separateStay = fillSeparateStay(data.checkInDate, data.checkOutDate, activeProduct);
    search.rooms = searchParamsRoomsParser(data, activeProduct);
    search.segments = searchParamsSegmentParser(data, search.tripType);

    const regexpOthers = new RegExp('^(class|adults|children|infants|resident|direct)');
    const otherKeys = Object.keys(data).filter((key) => key.match(regexpOthers));
    otherKeys.forEach((key) => {
        switch (key) {
            case 'class':
                search[keyMap[key]] = data[key];
                break;
            case 'adults':
            case 'children':
            case 'infants':
                search.pax[keyMap[key]] = parseInt(data[key]);
                break;
            case 'resident':
            case 'direct':
                search[keyMap[key]] = data[key] === 'true';
                break;
            default:
                break;
        }
    });
    return search;
};

const composeParametrizedDate = (type, value, depDate) => {
    let date;
    let datePlusOffset;
    let formatDate = (date) => {
        let day = date.getDate();
        let month = date.getMonth() + 1;
        let year = date.getFullYear();

        return year + '-' + (month < 10 ? '0' + month : month) + '-' + (day < 10 ? '0' + day : day);
    };

    if (type === 'dep') {
        date = new Date();
    } else {
        const [year, month, day] = depDate.split('-');
        date = new Date(year, month - 1, day);
    }
    datePlusOffset = date.getDate() + parseInt(value, 10);
    date.setDate(datePlusOffset);
    return formatDate(date);
};

const extendInternalSearch = () => ';internalSearch=true';

const extendBuyPath = (buyPath) => {
    return buyPath ? `;buyPath=${buyPath}` : '';
};

const extendTrainSearch = (currentSearch) => {
    return currentSearch.trainSearch ? ';trainSearch=true' : '';
};

const isInspirational = ({ segments }) => {
    const firstDest = segments[0] && segments[0].destination;
    return !!firstDest && firstDest.geoNodeId === 0;
};

const toOldFormat = (search) => {
    const getDate = (date) => (search.separateStay.enabled ? DateUtils.toString(date) : '');
    const getRooms = (isHotel) =>
        isHotel
            ? search.rooms.map((room) => ({
                numAdults: room.adults,
                infantsAges: room.kids.filter((age) => age < 2),
                kidsAges: room.kids.filter((age) => age > 1),
                childrenAges: room.kids,
            }))
            : [];

    const getSegments = () => {
        const { segments } = search;
        if (search.tripType === TripType.ROUND_TRIP) {
            const [first, second] = segments;
            second.origin = first.destination;
            second.destination = first.origin;
            return [first, second];
        }

        return segments;
    };

    return {
        priceTrends: search.priceTrends,
        cabinClass: search.cabinClass,
        creationDate: search.createdAt,
        searchSegments: getSegments().map((segment) => ({
            departure: segment.origin,
            destination: segment.destination,
            dateStr: segment.date && DateUtils.toString(segment.date),
        })),
        type: search.tripType,
        directFlightsOnly: search.isDirect,
        numAdults: search.pax.adults,
        numChildren: search.pax.kids,
        numInfants: search.pax.infants,
        resident: search.isResident,
        buyPath: search.buyPath,
        isInspirationalSearch: isInspirational(search),
        checkInDate: search.isHotel ? getDate(search.separateStay.checkInDate) : '',
        checkOutDate: search.isHotel ? getDate(search.separateStay.checkOutDate) : '',
        rooms: getRooms(search.isHotel),
    };
};

const toNewFormat = (search) => {
    try {
        return {
            createdAt: search.creationDate,
            priceTrends: search.priceTrends,
            cabinClass: search.cabinClass,
            segments: search.searchSegments.map((segment, i) => ({
                id: Date.now() + i,
                origin: segment.departure,
                destination: segment.destination,
                date: DateUtils.parse(segment.dateStr),
            })),
            tripType: search.type,
            isDirect: search.directFlightsOnly,
            pax: {
                adults: search.numAdults,
                kids: search.numChildren,
                infants: search.numInfants,
            },
            isResident: !!search.resident,
            buyPath: search.buyPath,
            separateStay: {
                enabled: !!search.checkInDate && !!search.checkOutDate,
                checkInDate: DateUtils.parse(search.checkInDate),
                checkOutDate: DateUtils.parse(search.checkOutDate),
            },
            rooms: search.rooms
                ? search.rooms.map((room, idx) =>
                    HotelRoomUtils.getModelRoom(
                        room.numAdults,
                        room.infantsAges.concat(room.kidsAges),
                        idx,
                    ),
                )
                : [],
        };
    } catch (e) {
        // TODO log the input on backend
        return SearchProp.defaultProps;
    }
};

const getHotelsHomeSearchURL = ({ searchExtensionFlag = false }) => {
    const historyConfig ={
        ...searchConfigByProduct[SearchProducts.HOTELS_HOME],
        search: '?searchExtension=true', // only used by getDeeplinkSearchURL
    };
    const urlBuilder = searchExtensionFlag ? getDeeplinkSearchURL : getDeeplinkURL;
    return urlBuilder(historyConfig, '');
};

const getSearchURL = (currentSearch, useQueryParam, { searchExtensionFlag = false } = {}) => {
    let params;
    let historyConfig;

    if (isInspirationalSearch({ currentSearch })) {
        historyConfig = searchConfigByProduct[SearchProducts.INSPIRATIONAL];
        params = composeFlightsParams(currentSearch);
    } else if (currentSearch.hotelHome) {
        return getHotelsHomeSearchURL({ searchExtensionFlag });
    } else if (currentSearch.hotelStandalone) {
        return getHotelStandaloneSearchURL(currentSearch, useQueryParam);
    } else if (currentSearch.dynPack) {
        historyConfig = searchConfigByProduct[SearchProducts.DYNPACK];
        params = composeDynpackParams(currentSearch);
    } else {
        historyConfig = searchConfigByProduct[SearchProducts.FLIGHTS];
        params = composeFlightsParams(currentSearch);
    }

    const getDeeplinkFn = useQueryParam && historyConfig.queryParam ? getDeeplinkQueryURL : getDeeplinkURL;

    return getDeeplinkFn(historyConfig, params)
        .concat(extendBuyPath(currentSearch.buyPath))
        .concat(extendInternalSearch())
        .concat(extendTrainSearch(currentSearch));
};

const getHotelStandaloneSearchURL = (currentSearch, useQueryParam) => {
    let historyConfig = searchConfigByProduct[SearchProducts.HOTEL_STANDALONE];
    historyConfig.search = URLUtils.getSearch();
    const searchExtensionParam = historyConfig.search === '?searchExtension=true/' ? ';searchExtension=true' : '';

    const { type, id } = currentSearch.segments[0].destination;
    let params = id && type in GeoType
        ? composeHotelStandaloneByRegionParams({ ...currentSearch, searchExtensionParam })
        : composeHotelStandaloneParams({ ...currentSearch, searchExtensionParam });

    const getDeeplinkFn = useQueryParam && historyConfig.queryParam ? getDeeplinkQueryURL : getMFEDeeplinkURL;
    return getDeeplinkFn(historyConfig, params);
};

const parseSearchExtension = (currentSearch = {}, customDestination) => {
    const { segments = [], tripType, pax } = currentSearch;
    const firstSegmentDate = DateUtils.stringToDate(segments[0]?.date);
    const secondSegmentDate = DateUtils.stringToDate(segments[1]?.date);
    const isSecondSegmentDatePastFirstSegmentDate =
        DateUtils.compare(firstSegmentDate, secondSegmentDate) > 0;
    const isRoundTrip = tripType === TripType.ROUND_TRIP;

    const checkInDate = firstSegmentDate;
    let checkOutDate;
    if (isRoundTrip && isSecondSegmentDatePastFirstSegmentDate) {
        const isExceedingMaxDays =
            DateUtils.getDifferenceInDays(firstSegmentDate, secondSegmentDate) >
            DateUtils.MAX_DAYS_PRIME_HOTEL;
        checkOutDate = isExceedingMaxDays
            ? DateUtils.add(checkInDate, DateUtils.MAX_DAYS_PRIME_HOTEL)
            : secondSegmentDate;
    } else {
        checkOutDate = DateUtils.add(checkInDate, 1);
    }
    return {
        ...currentSearch,
        segments: [
            {
                date: checkInDate,
                destination: customDestination || segments[0].destination,
            },
            {
                date: checkOutDate,
            },
        ],
        rooms: [
            {
                adults: pax.adults + pax.kids,
                kids: [],
            },
        ],
    };
};

const getSearchExtensionURL = ({ currentSearch, urlBuilder = getSearchURL } = {}) => {
    const adjustedSearchData = parseSearchExtension(currentSearch);
    const additionalParams = currentSearch.hotelHome ? { searchExtensionFlag: true } : {};
    return urlBuilder({
        ...adjustedSearchData,
        buyPath: currentSearch.hotelHome ? '' : BuyPath.HSA_PB_SEARCHEXTENSION,
    }, undefined, additionalParams);
};

export const CARS_SEARCH_CLIENT_IDS = {
    FLIGHTS: {
        [`${TripType.ONE_WAY}`]: {
            2: '607481',
            3: '607498',
        },
        [`${TripType.ROUND_TRIP}`]: {
            2: '606852',
            3: '606869',
        },
        DP: {
            2: '606886',
            3: '606903',
        },
    },
    DP: {
        [`${TripType.ROUND_TRIP}`]: {
            2: '606920',
            3: '606937',
        },
    },
    HOTELS: {
        [`${TripType.ROUND_TRIP}`]: {
            2: '606954',
            3: '606971',
        },
    },
};

export const getCarsSearchExtensionURL = ({ currentSearch, activeProduct, dynPack }) => {
    const { carsSearchExtensionVariation } = ABUtils.getABs();

    const {
        segments: [{ date: pickupDate, destination }, { date: returnDate } = {}],
    } = currentSearch;

    let { tripType } = currentSearch;
    if (activeProduct === 'flights' && dynPack) {
        tripType = 'DP';
    }

    const clientId =
        CARS_SEARCH_CLIENT_IDS[activeProduct.toUpperCase()][tripType][carsSearchExtensionVariation];

    const baseUrl = new URL(i18next.t('home.services.cars.url')).origin;
    const url = new URL(`${baseUrl}/${Config.get('lang')}/book?`);

    url.searchParams.append('clientId', clientId);
    url.searchParams.append('pickupName', destination.name);
    url.searchParams.append('returnName', destination.name);
    url.searchParams.append(
        'pickupDateTime',
        `${pickupDate.getFullYear()}-${pickupDate.getMonth() + 1}-${pickupDate.getDate()}T10:00:00`,
    );

    const returnDateURL = returnDate
        ? returnDate
        : new Date(pickupDate.getFullYear(), pickupDate.getMonth(), pickupDate.getDate() + 5);
    url.searchParams.append(
        'returnDateTime',
        `${returnDateURL.getFullYear()}-${
            returnDateURL.getMonth() + 1
        }-${returnDateURL.getDate()}T10:00:00`,
    );

    url.searchParams.append('currency', Config.get('i18n')?.currency?.currencyCode);

    if (destination.relatedLocations?.length !== 1 && destination.coordinates) {
        url.searchParams.append('pickupLat', destination.coordinates.latitude);
        url.searchParams.append('pickupLng', destination.coordinates.longitude);
        url.searchParams.append('returnLat', destination.coordinates.latitude);
        url.searchParams.append('returnLng', destination.coordinates.longitude);
    }

    return url.toString();
};

export const SearchService = {
    searchParamsParser,
    toOldFormat,
    toNewFormat,
    isInspirational,
    getSearchURL,
    getSearchExtensionURL,
    getCarsSearchExtensionURL,
};
